/**
 * Destination dossier side sheet.
 *
 * Renders the latest dossier's markdown (if ready) plus a citation list, or a
 * polling spinner while the server generates one. Empty state offers a
 * Generate button; errored state offers a retry.
 *
 * The parent decides when the sheet is open; this component is otherwise
 * self-sufficient — it owns the query + mutation via `useDossier`.
 */

import ReactMarkdown from 'react-markdown';
import { RefreshCw, Loader2, AlertCircle, FileText } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetBody,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useDossier } from '../hooks/useDossier';
import { sanitizeSourceUrl } from './TravelHelpers';

interface TravelDossierPanelProps {
  tripId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TravelDossierPanel({ tripId, open, onOpenChange }: TravelDossierPanelProps) {
  const { dossier, loading, error, isPending, isGenerating, generate, featureEnabled } =
    useDossier(tripId);

  const generatedAt = dossier?.createdAt
    ? new Date(dossier.createdAt).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col gap-0">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" /> Destination dossier
          </SheetTitle>
          <SheetDescription>
            AI-generated travel brief with visa, weather, currency, safety, and more. Sources are
            pulled live from the web.
          </SheetDescription>
        </SheetHeader>

        <SheetBody>
          <div className="mt-4 space-y-4">
            {!featureEnabled ? (
              <div className="text-sm text-muted-foreground">
                The dossier feature is disabled. Re-enable it from Settings → Dossier to generate a
                destination brief.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground">
                    {dossier ? `Last generated ${generatedAt}` : 'No dossier yet for this trip.'}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => void generate()}
                    disabled={isPending || isGenerating}
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Working…
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-3 w-3 mr-1" /> {dossier ? 'Regenerate' : 'Generate'}
                      </>
                    )}
                  </Button>
                </div>

                {loading && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Checking for an existing dossier…
                  </div>
                )}

                {error && (
                  <div className="flex items-start gap-2 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>Could not load dossier: {error}</span>
                  </div>
                )}

                {isPending && (
                  <div className="rounded-md border border-border p-4 bg-muted/50">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Loader2 className="h-4 w-4 animate-spin" /> Researching your destinations…
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      This takes 20–40 seconds because the assistant runs real web searches for each
                      section. You can close this panel; the dossier will be ready when you reopen
                      it.
                    </p>
                  </div>
                )}

                {dossier?.status === 'error' && (
                  <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                      <div>
                        <div className="font-medium">Dossier generation failed.</div>
                        {dossier.errorMessage && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {dossier.errorMessage}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground mt-2">
                          Tap Regenerate to try again.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {dossier?.status === 'ready' && dossier.markdown && (
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown>{dossier.markdown}</ReactMarkdown>
                  </div>
                )}

                {dossier?.status === 'ready' && dossier.sources.length > 0 && (
                  <div className="border-t border-border pt-4">
                    <div className="text-sm font-semibold mb-2">Sources</div>
                    <ol className="space-y-1 text-xs list-decimal list-inside">
                      {dossier.sources
                        .map((src) => ({
                          safeUrl: sanitizeSourceUrl(src.url),
                          title: src.title,
                        }))
                        .filter(
                          (src): src is { safeUrl: string; title: string | undefined } =>
                            src.safeUrl !== null,
                        )
                        .map((src, i) => (
                          <li key={`${src.safeUrl}-${i}`} className="break-all">
                            <a
                              href={src.safeUrl}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="text-primary hover:underline"
                            >
                              {src.title || src.safeUrl}
                            </a>
                          </li>
                        ))}
                    </ol>
                  </div>
                )}
              </>
            )}
          </div>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
